/**
 * Upscaling-presets hook. Wraps the list query plus create/update/delete
 * mutations; all mutations invalidate `qk.upscalingPresets()` on success so the
 * list refetches. Presets are global/shared — the list is the same for everyone;
 * `can_edit` on each preset gates the update/delete controls in the UI.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createUpscalingPreset,
  deleteUpscalingPreset,
  listUpscalingPresets,
  updateUpscalingPreset,
} from '../lib/endpoints/upscalingPresets'
import { qk } from '../lib/queryKeys'
import type { UpscalingPreset, UpscalingPresetSpec } from '../lib/schemas'

export function useUpscalingPresets() {
  const qc = useQueryClient()

  const query = useQuery({
    queryKey: qk.upscalingPresets(),
    queryFn: listUpscalingPresets,
  })

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: qk.upscalingPresets() })

  const create = useMutation<
    UpscalingPreset,
    Error,
    { name: string; spec: UpscalingPresetSpec; isPublic?: boolean }
  >({
    mutationFn: ({ name, spec, isPublic }) =>
      createUpscalingPreset(name, spec, isPublic),
    onSuccess: () => invalidate(),
  })

  const update = useMutation<
    UpscalingPreset,
    Error,
    { id: string; name: string; spec: UpscalingPresetSpec; isPublic?: boolean }
  >({
    mutationFn: ({ id, name, spec, isPublic }) =>
      updateUpscalingPreset(id, name, spec, isPublic),
    onSuccess: () => invalidate(),
  })

  const remove = useMutation<void, Error, string>({
    mutationFn: (id: string) => deleteUpscalingPreset(id),
    onSuccess: () => invalidate(),
  })

  return { query, create, update, remove }
}
